import { useMemo, useState } from 'react'
import { ChevronRight, Folder, Grid2X2, Home, List, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import WorkspaceFileCard from '@/frontend/features/Fde/WorkspaceFileCard'
import type { FdeCopy, RepositoryFile } from '@/frontend/features/Fde/types'
import { cn } from '@/lib/utils'

interface FolderEntry { name: string; path: string; count: number }

function directoryContents(files: RepositoryFile[], directory: string, query: string) {
  const normalizedQuery = query.trim().toLocaleLowerCase('pt-BR')
  if (normalizedQuery) return {
    folders: [] as FolderEntry[],
    files: files.filter((file) => `${file.name} ${file.path}`.toLocaleLowerCase('pt-BR').includes(normalizedQuery)),
  }

  const prefix = directory ? `${directory}/` : ''
  const folders = new Map<string, FolderEntry>()
  const visibleFiles: RepositoryFile[] = []
  files.forEach((file) => {
    if (!file.path.startsWith(prefix)) return
    const relative = file.path.slice(prefix.length)
    const [first, ...rest] = relative.split('/')
    if (rest.length === 0) visibleFiles.push(file)
    else {
      const path = `${prefix}${first}`
      const entry = folders.get(path) || { name: first, path, count: 0 }
      entry.count += 1
      folders.set(path, entry)
    }
  })
  return {
    folders: [...folders.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    files: visibleFiles.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
  }
}

export default function WorkspaceBrowser({ files, copy, onOpen }: { files: RepositoryFile[]; copy: FdeCopy; onOpen: (path: string) => void }) {
  const [directory, setDirectory] = useState('')
  const [query, setQuery] = useState('')
  const [view, setView] = useState<'grid' | 'list'>(() => (typeof window !== 'undefined' && window.localStorage.getItem('fde-workspace-view') === 'list' ? 'list' : 'grid'))
  const contents = useMemo(() => directoryContents(files, directory, query), [files, directory, query])
  const crumbs = directory.split('/').filter(Boolean)

  function chooseView(next: 'grid' | 'list') {
    setView(next)
    window.localStorage.setItem('fde-workspace-view', next)
  }

  return <section className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
    <div className="shrink-0 border-b border-border bg-card px-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.material.search_files} className="pl-9" /></div>
        <div className="flex rounded-lg border border-border bg-muted/20 p-0.5">
          <Button size="icon-sm" variant={view === 'grid' ? 'secondary' : 'ghost'} onClick={() => chooseView('grid')} aria-label={copy.material.grid_view}><Grid2X2 /></Button>
          <Button size="icon-sm" variant={view === 'list' ? 'secondary' : 'ghost'} onClick={() => chooseView('list')} aria-label={copy.material.list_view}><List /></Button>
        </div>
      </div>
      <nav className="mt-3 flex items-center gap-1 overflow-x-auto text-xs text-muted-foreground">
        <button type="button" onClick={() => setDirectory('')} className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-muted hover:text-foreground"><Home className="size-3.5" />{copy.material.workspace_root}</button>
        {crumbs.map((crumb, index) => <span key={crumb} className="flex items-center gap-1"><ChevronRight className="size-3" /><button type="button" onClick={() => setDirectory(crumbs.slice(0, index + 1).join('/'))} className="rounded px-1.5 py-1 hover:bg-muted hover:text-foreground">{crumb}</button></span>)}
        <span className="ml-auto shrink-0">{copy.material.item_count.replace('%{count}', String(contents.folders.length + contents.files.length))}</span>
      </nav>
    </div>
    <div className="min-h-0 flex-1 overflow-y-auto p-5 [scrollbar-gutter:stable]">
      {contents.folders.length > 0 && <div className={cn('mb-5', view === 'grid' ? 'grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3' : 'divide-y divide-border border-y border-border')}>
        {contents.folders.map((folder) => <button key={folder.path} type="button" onClick={() => { setDirectory(folder.path); setQuery('') }} className={cn('flex items-center gap-3 text-left hover:bg-muted/50', view === 'grid' ? 'rounded-xl border border-border bg-card p-3.5 shadow-xs hover:border-primary/30' : 'w-full px-3 py-2.5')}><span className="flex size-10 items-center justify-center rounded-xl bg-primary/8 text-primary"><Folder className="size-5" /></span><span className="min-w-0"><strong className="block truncate text-sm font-medium">{folder.name}</strong><span className="text-[11px] text-muted-foreground">{copy.material.folder_file_count.replace('%{count}', String(folder.count))}</span></span></button>)}
      </div>}
      {view === 'list' && contents.files.length > 0 && <div className="grid grid-cols-[minmax(0,1fr)_minmax(8rem,0.45fr)_7rem_2rem] gap-3 border-b border-border px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><span>{copy.material.file_name}</span><span>{copy.material.added_by}</span><span>{copy.material.size}</span><span /></div>}
      <div className={cn(view === 'grid' ? 'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3' : '')}>
        {contents.files.map((file) => <WorkspaceFileCard key={file.path} file={file} view={view} copy={copy} onOpen={() => onOpen(file.path)} />)}
      </div>
      {contents.folders.length === 0 && contents.files.length === 0 && <div className="flex h-56 flex-col items-center justify-center text-center"><Search className="mb-3 size-8 text-muted-foreground/40" /><p className="text-sm font-medium">{copy.material.empty_search}</p><p className="mt-1 text-xs text-muted-foreground">{copy.material.empty_search_hint}</p></div>}
    </div>
  </section>
}
